import { computed } from 'vue';

import { talkE911GapCount, useTalkE911Gaps } from './talk-e911';
import { talkEmergencyActiveCount, useTalkEmergencyActive } from './talk-emergency';
import { isTalkOnboardingActive, useIsTalkOnboardingActive } from './talk-onboarding';
import { talkExtensionsMissingCallerIdCount, useTalkExtensionsMissingCallerId } from './talk-outbound';
import { talkUnroutedNumberCount, useTalkUnroutedNumbers } from './talk-routing';
import { talkNeedsSmsRegistration, useTalkNeedsSmsRegistration } from './talk-sms-registration';

/**
 * Whether anything under Admin is waiting on this user — the single signal behind the attention dot
 * on the Admin sidebar entry and the Admin tile on the welcome screen.
 *
 * One aggregate rather than a check per surface: the dot means "there is something in here", and the
 * screens that render it shouldn't have to know what. Adding a source is an `||` here plus its load
 * in `useAdminAttention` — nothing else changes. Each source is expected to gate its own permissions
 * (see `isTalkOnboardingActive`), so this stays false for a user who could never act on it.
 */
export const adminNeedsAttention = computed(
    () =>
        adminHasFault.value ||
        isTalkOnboardingActive.value ||
        talkNeedsSmsRegistration.value ||
        talkUnroutedNumberCount.value > 0 ||
        talkExtensionsMissingCallerIdCount.value > 0
);

/**
 * The subset of the above that is broken rather than merely outstanding — surfaces that can show a
 * red exclamation use this to pick the stronger marker, and everything else keeps treating it as
 * part of the aggregate.
 *
 * Emergency calling only, by design: the red treatment means "911 won't work, or a 911 call is still
 * being answered for". Everything else — including an extension that can't dial out at all — raises
 * the attention dot instead, however severe it is, so the one marker that means life-safety keeps
 * meaning exactly that.
 */
export const adminHasFault = computed(() => talkE911GapCount.value > 0 || talkEmergencyActiveCount.value > 0);

/** Returns the aggregate, kicking every source's (gated, deduped) load if it hasn't run yet. */
export function useAdminAttention() {
    useIsTalkOnboardingActive();
    useTalkE911Gaps();
    useTalkEmergencyActive();
    useTalkExtensionsMissingCallerId();
    useTalkNeedsSmsRegistration();
    useTalkUnroutedNumbers();
    return adminNeedsAttention;
}
